import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { z } from "zod";
import type { Site } from "@chat-yai/shared/admin-types";
import { sitesApi } from "@/api/endpoints/sites";
import { tenantsApi } from "@/api/endpoints/tenants";
import { Modal } from "@/components/Modal";
import { FormError } from "@/components/FormError";
import { Icon } from "@/components/Icon";
import { useToast } from "@/contexts/ToastContext";
import { ApiError } from "@/api/client";
import { translateError } from "@/i18n/errors";
import { canManageContent, canManageBilling, permissionTooltip } from "@/lib/rbac";
import { EmptyState } from "@/components/EmptyState";

// Domain syntax: lowercase host, optional leading "*.", at most 253 chars.
// Matches the rule used by tenant_sites.domain in docs/plans/wp-b.md §4.1.
const DOMAIN_RE = /^(?:\*\.)?(?:[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?)(?:\.[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?)+$/;

const createSchema = z.object({
  name: z.string().trim().min(1, "Name is required."),
  domain: z
    .string()
    .trim()
    .toLowerCase()
    .max(253, "Domain too long.")
    .refine((v) => DOMAIN_RE.test(v), {
      message: "Use a host like example.co.nz or *.example.co.nz.",
    }),
});
type CreateValues = z.infer<typeof createSchema>;

export function SitesPage(): JSX.Element {
  const queryClient = useQueryClient();
  const toast = useToast();
  const [createOpen, setCreateOpen] = useState(false);
  const [pendingDelete, setPendingDelete] = useState<Site | null>(null);
  const [confirmText, setConfirmText] = useState("");

  const currentQuery = useQuery({
    queryKey: ["tenants", "current"],
    queryFn: () => tenantsApi.current(),
  });
  const role = currentQuery.data?.role ?? "viewer";
  const canEdit = canManageContent(role);
  const canDelete = canManageBilling(role);

  const sitesQuery = useQuery({
    queryKey: ["sites"],
    queryFn: () => sitesApi.list(),
  });

  const form = useForm<CreateValues>({ defaultValues: { name: "", domain: "" } });

  const createMutation = useMutation({
    mutationFn: (v: CreateValues) => sitesApi.create(createSchema.parse(v)),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["sites"] });
      setCreateOpen(false);
      form.reset();
      toast.push("success", "Site added.");
    },
    onError: (err) => {
      toast.push("error", translateError(err instanceof ApiError ? err.code : undefined));
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => sitesApi.remove(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["sites"] });
      setPendingDelete(null);
      setConfirmText("");
      toast.push("success", "Site deleted.");
    },
    onError: (err) => {
      toast.push("error", translateError(err instanceof ApiError ? err.code : undefined));
    },
  });

  return (
    <div className="page" data-testid="sites-page">
      <div className="page-head">
        <div>
          <div className="page-title">Sites</div>
          <div className="page-sub">
            Domains where your chat widget is allowed to load. Wildcards like
            <code style={{ marginLeft: 6 }}>*.example.co.nz</code> match every
            subdomain (but not the apex).
          </div>
        </div>
        {canEdit ? (
          <div className="page-actions">
            <button
              className="btn btn-accent btn-sm"
              onClick={() => setCreateOpen(true)}
              data-testid="open-add-site"
            >
              <Icon name="plus" size={12} /> Add site
            </button>
          </div>
        ) : null}
      </div>

      <div className="card">
        <div
          className="table-row-head"
          style={{ gridTemplateColumns: "1.6fr 2fr 1fr 160px 120px" }}
        >
          <div>Name</div>
          <div>Domain</div>
          <div>Status</div>
          <div>Verified</div>
          <div></div>
        </div>
        {sitesQuery.data?.sites.map((s) => (
          <div
            key={s.id}
            className="list-row"
            style={{ gridTemplateColumns: "1.6fr 2fr 1fr 160px 120px" }}
            data-testid={`site-row-${s.domain}`}
          >
            <div style={{ fontWeight: 500, fontSize: 13 }}>{s.name}</div>
            <div className="mono" style={{ fontSize: 12 }}>
              {s.domain}
            </div>
            <div>
              <span className={"chip " + (s.status === "active" ? "chip-success" : "chip-warning")}>
                {s.status}
              </span>
            </div>
            <div className="mono" style={{ fontSize: 11, color: "var(--text-3)" }}>
              {s.verified_at ? new Date(s.verified_at).toLocaleDateString() : "—"}
            </div>
            <div style={{ display: "flex", justifyContent: "flex-end" }}>
              {canDelete ? (
                <button
                  className="btn btn-ghost btn-sm"
                  onClick={() => setPendingDelete(s)}
                  data-testid={`delete-site-${s.domain}`}
                >
                  Delete
                </button>
              ) : (
                <button
                  className="btn btn-ghost btn-sm"
                  disabled
                  title={permissionTooltip("owner")}
                >
                  Delete
                </button>
              )}
            </div>
          </div>
        ))}
        {(sitesQuery.data?.sites.length ?? 0) === 0 ? (
          <EmptyState
            icon="globe"
            title="No sites yet"
            description="Sites are the domains where your chat widget is allowed to load. Add your apex (e.g. example.co.nz) plus any wildcard subdomain you want to cover."
            primary={
              canEdit
                ? {
                    label: "Add your first site",
                    onClick: () => setCreateOpen(true),
                    testId: "empty-sites-add",
                  }
                : undefined
            }
            testId="empty-sites"
          />
        ) : null}
      </div>

      <Modal
        title="Add site"
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        footer={
          <>
            <button className="btn btn-ghost btn-sm" onClick={() => setCreateOpen(false)}>
              Cancel
            </button>
            <button
              className="btn btn-primary btn-sm"
              disabled={createMutation.isPending}
              onClick={form.handleSubmit((v) => createMutation.mutate(v))}
              data-testid="submit-add-site"
            >
              {createMutation.isPending ? "Saving…" : "Add site"}
            </button>
          </>
        }
      >
        <div className="field-group">
          <label>Display name</label>
          <input
            className="field-input"
            placeholder="Public website"
            data-testid="site-name"
            {...form.register("name")}
          />
          <FormError message={form.formState.errors.name?.message} />
        </div>
        <div className="field-group">
          <label>Domain</label>
          <input
            className="field-input mono"
            placeholder="example.co.nz or *.example.co.nz"
            data-testid="site-domain"
            {...form.register("domain")}
          />
          <FormError message={form.formState.errors.domain?.message} />
          <div className="help">
            Lowercase host. Use <code>*.example.co.nz</code> to allow every
            subdomain. The apex domain still needs its own row.
          </div>
        </div>
      </Modal>

      <Modal
        title="Delete site"
        open={!!pendingDelete}
        onClose={() => {
          setPendingDelete(null);
          setConfirmText("");
        }}
        footer={
          <>
            <button
              className="btn btn-ghost btn-sm"
              onClick={() => {
                setPendingDelete(null);
                setConfirmText("");
              }}
            >
              Cancel
            </button>
            <button
              className="btn btn-danger btn-sm"
              disabled={
                deleteMutation.isPending ||
                !pendingDelete ||
                confirmText !== pendingDelete.domain
              }
              onClick={() => pendingDelete && deleteMutation.mutate(pendingDelete.id)}
              data-testid="confirm-delete-site"
            >
              {deleteMutation.isPending ? "Deleting…" : "Delete site"}
            </button>
          </>
        }
      >
        {pendingDelete ? (
          <>
            <p>
              This permanently removes <strong>{pendingDelete.name}</strong> and
              prevents the widget from loading on{" "}
              <span className="mono">{pendingDelete.domain}</span>. Chatbots
              attached to this site must be reassigned first.
            </p>
            <div className="field-group" style={{ marginTop: 8 }}>
              <label>
                Type the domain (<span className="mono">{pendingDelete.domain}</span>) to confirm
              </label>
              <input
                className="field-input mono"
                value={confirmText}
                onChange={(e) => setConfirmText(e.target.value)}
                data-testid="confirm-delete-text"
              />
            </div>
          </>
        ) : null}
      </Modal>
    </div>
  );
}
